import {
  Bell,
  Palette,
  Plus,
  Search,
  Share2,
  Phone,
  Mail,
  Globe,
  Linkedin,
  MapPin,
  QrCode,
  ChevronRight,
  RefreshCw,
  Check,
  Apple,
  Briefcase,
  Heart,
  User,
  Home as HomeIcon,
  Users,
  LayoutGrid,
  Settings,
  ArrowUp,
} from "lucide-react";

const PHOTOS = {
  maya: "/mock/maya.jpg",
  leo: "/mock/leo.jpg",
  priya: "/mock/priya.jpg",
  sam: "/mock/sam.jpg",
  ivy: "/mock/ivy.jpg",
  swimmers: "/mock/swimmers.jpg",
  family: "/mock/family.jpg",
  friends: "/mock/friends.jpg",
};

// Color themes mirror the in-app theme picker (the palette icon in the header).
export const THEMES = {
  ocean: {
    key: "ocean", name: "Ocean", dark: false,
    bar: "linear-gradient(160deg,#16233f,#22345c)",
    page: "#eef2f8", card: "#ffffff", accent: "#2f6bd6", accentSoft: "#e5edfb",
    heading: "#0f172a", sub: "#64748b", barText: "#ffffff", border: "#e2e8f0",
  },
  rose: {
    key: "rose", name: "Rose", dark: false,
    bar: "linear-gradient(160deg,#c14d6b,#e07a94)",
    page: "#fbe9ee", card: "#ffffff", accent: "#c14d6b", accentSoft: "#f7d7e0",
    heading: "#3f1622", sub: "#9a5a6c", barText: "#ffffff", border: "#f1d3db",
  },
  sepia: {
    key: "sepia", name: "Sepia", dark: false,
    bar: "linear-gradient(160deg,#6b4423,#8a5a33)",
    page: "#efe7d6", card: "#fbf6ea", accent: "#8a5a33", accentSoft: "#e6d6bd",
    heading: "#3a2a17", sub: "#8a765a", barText: "#ffffff", border: "#e0d3ba",
  },
  indigo: {
    key: "indigo", name: "Indigo", dark: true,
    bar: "linear-gradient(160deg,#2a2560,#3b338a)",
    page: "#1e1b4b", card: "#2a2660", accent: "#8b82f0", accentSoft: "#3a3480",
    heading: "#f1f0ff", sub: "#b7b2e8", barText: "#ffffff", border: "rgba(255,255,255,0.08)",
  },
};

const AV = ["#2f6bd6", "#10b981", "#8b5cf6", "#ef4444", "#f59e0b", "#06b6d4", "#ec4899"];

// Realistic-looking QR code art (cosmetic only, not a functional scan target).
function QrArt({ size = 88, fg = "#1e1b4b" }) {
  const N = 21;
  const finderOn = (r, c, r0, c0) => {
    const rr = r - r0;
    const cc = c - c0;
    if (rr === 0 || rr === 6 || cc === 0 || cc === 6) return true;
    return rr >= 2 && rr <= 4 && cc >= 2 && cc <= 4;
  };
  const cells = [];
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      let on;
      if (r < 7 && c < 7) on = finderOn(r, c, 0, 0);
      else if (r < 7 && c > N - 8) on = finderOn(r, c, 0, N - 7);
      else if (r > N - 8 && c < 7) on = finderOn(r, c, N - 7, 0);
      else if (r === 6 || c === 6) on = (r + c) % 2 === 0;
      else on = (r * 928371 + c * 123457 + r * c * 31) % 7 < 3;
      if (on) cells.push(<rect key={`${r}-${c}`} x={c} y={r} width={1} height={1} fill={fg} />);
    }
  }
  return (
    <svg viewBox={`0 0 ${N} ${N}`} width={size} height={size} shapeRendering="crispEdges">
      <rect x={0} y={0} width={N} height={N} fill="#ffffff" />
      {cells}
    </svg>
  );
}

function Wordmark({ size = "text-[13px]" }) {
  return (
    <span className={`font-wordmark font-semibold tracking-tight ${size}`}>
      people
      <span style={{ color: "#f472b6", fontSize: "1.3em", margin: "0 0.03em" }} className="font-bold">
        i
      </span>
      know
    </span>
  );
}

function Avatar({ label, color, size = 44, img }) {
  const initials = label
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold shrink-0 overflow-hidden"
      style={{ width: size, height: size, background: color, fontSize: size * 0.36 }}
    >
      {img ? <img src={img} alt={label} className="h-full w-full object-cover" /> : initials}
    </div>
  );
}

export function PhoneFrame({ children, className = "", testId }) {
  return (
    <div className={`mx-auto w-full max-w-[290px] ${className}`} data-testid={testId}>
      <div className="rounded-[2.6rem] border-[8px] border-slate-900 bg-slate-900 shadow-[0_30px_60px_-20px_rgba(15,23,42,0.5)] p-0">
        <div className="relative rounded-[2rem] overflow-hidden bg-white flex flex-col" style={{ aspectRatio: "9 / 19.3" }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 mt-2 h-4 w-24 rounded-full bg-slate-900" />
          {children}
        </div>
      </div>
    </div>
  );
}

function StatusBar({ t }) {
  return (
    <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[10px] font-semibold shrink-0" style={{ color: t.barText }}>
      <span>9:41</span>
      <span className="tracking-widest opacity-80">••• ▓ 100</span>
    </div>
  );
}

function TopBar({ t, back }) {
  return (
    <div className="flex items-center justify-between px-4 pt-1 pb-3 shrink-0" style={{ color: t.barText }}>
      <div className="flex items-center gap-2">
        {back && <ChevronRight className="h-4 w-4 rotate-180 opacity-90" />}
        <Avatar label="Jordan Avery" color="#0ea5e9" size={26} />
      </div>
      <Wordmark />
      <div className="flex items-center gap-2.5 opacity-90">
        <Bell className="h-4 w-4" />
        <Palette className="h-4 w-4" />
      </div>
    </div>
  );
}

function Tabs({ t, active = "Home" }) {
  const items = [
    { icon: HomeIcon, label: "Home" },
    { icon: Users, label: "Contacts" },
    { icon: LayoutGrid, label: "Groups" },
    { icon: QrCode, label: "Intros" },
    { icon: Settings, label: "Settings" },
  ];
  return (
    <div className="mt-auto flex items-center justify-between px-4 py-2.5 border-t shrink-0"
      style={{ background: t.card, borderColor: t.border }}>
      {items.map((it) => {
        const Icon = it.icon;
        const on = it.label === active;
        return (
          <div key={it.label} className="flex flex-col items-center gap-0.5">
            <Icon className="h-[15px] w-[15px]" style={{ color: on ? t.accent : t.sub }} />
            <span className="text-[7px] font-medium" style={{ color: on ? t.accent : t.sub }}>{it.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export function HomeScreen({ theme: t = THEMES.ocean }) {
  const favs = [
    { n: "Maya", img: PHOTOS.maya },
    { n: "Leo", img: PHOTOS.leo },
    { n: "Priya", img: PHOTOS.priya },
    { n: "Sam", img: PHOTOS.sam },
    { n: "Ivy", img: PHOTOS.ivy },
  ];
  const recent = [
    { n: "Rachel", c: AV[2] },
    { n: "Tony", c: AV[0] },
    { n: "Ana", img: PHOTOS.ivy },
    { n: "Keith", img: PHOTOS.leo },
    { n: "Nina", img: PHOTOS.priya },
  ];
  const groups = [
    { n: "Swimmers", p: "7 people", img: PHOTOS.swimmers },
    { n: "Family", p: "9 people", img: PHOTOS.family },
    { n: "Close friends", p: "15 people", img: PHOTOS.friends },
  ];
  return (
    <div style={{ background: t.page }} className="h-full flex flex-col">
      <div style={{ background: t.bar }} className="pb-4 rounded-b-3xl shrink-0">
        <StatusBar t={t} />
        <TopBar t={t} />
        <div className="px-4">
          <p className="text-[11px] opacity-80" style={{ color: t.barText }}>Still going strong,</p>
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-bold" style={{ color: t.barText }}>Jordan Avery</h4>
            <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center">
              <Plus className="h-4 w-4" style={{ color: t.accent }} />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 rounded-full bg-white/15 px-3 py-2">
              <Search className="h-3.5 w-3.5" style={{ color: t.barText, opacity: 0.8 }} />
              <span className="text-[11px]" style={{ color: t.barText, opacity: 0.85 }}>Search 1,385 contacts…</span>
            </div>
            <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center">
              <QrCode className="h-4 w-4" style={{ color: t.accent }} />
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 flex-1 overflow-hidden flex flex-col gap-4">
        <div>
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-bold" style={{ color: t.heading }}>Favorites</h5>
            <span className="text-[10px] font-semibold" style={{ color: t.accent }}>See all</span>
          </div>
          <div className="mt-2.5 flex justify-between">
            {favs.map((f) => (
              <div key={f.n} className="flex flex-col items-center gap-1">
                <Avatar label={f.n} color={AV[0]} img={f.img} size={40} />
                <span className="text-[9px]" style={{ color: t.sub }}>{f.n}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-bold" style={{ color: t.heading }}>Your Groups</h5>
            <span className="text-[10px] font-semibold" style={{ color: t.accent }}>See all</span>
          </div>
          <div className="mt-2.5 flex gap-2.5 overflow-hidden">
            {groups.map((g) => (
              <div key={g.n} className="w-1/2 shrink-0 rounded-xl overflow-hidden border" style={{ background: t.card, borderColor: t.border }}>
                <div className="h-14 bg-slate-200">
                  <img src={g.img} alt={g.n} className="h-full w-full object-cover" />
                </div>
                <div className="px-2.5 py-2">
                  <p className="text-[11px] font-bold leading-tight" style={{ color: t.heading }}>{g.n}</p>
                  <p className="text-[9px]" style={{ color: t.sub }}>{g.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-bold" style={{ color: t.heading }}>Recently Added</h5>
            <span className="text-[10px] font-semibold" style={{ color: t.accent }}>View all</span>
          </div>
          <div className="mt-2.5 flex justify-between">
            {recent.map((r) => (
              <div key={r.n} className="flex flex-col items-center gap-1">
                <Avatar label={r.n} color={r.c || AV[1]} img={r.img} size={40} />
                <span className="text-[9px]" style={{ color: t.sub }}>{r.n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Tabs t={t} active="Home" />
    </div>
  );
}

export function CardScreen({ theme: t = THEMES.ocean }) {
  const rows = [
    { icon: Phone, v: "(403) 555-0142" },
    { icon: Mail, v: "jordan@northwind.studio" },
    { icon: Globe, v: "www.peopleiknow.ai" },
    { icon: Linkedin, v: "LinkedIn" },
    { icon: MapPin, v: "Calgary, AB" },
  ];
  return (
    <div style={{ background: t.page }} className="h-full flex flex-col">
      <div style={{ background: t.bar }} className="pb-3 shrink-0">
        <StatusBar t={t} />
        <TopBar t={t} back />
      </div>
      <div className="px-4 pt-3 flex-1 overflow-hidden">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-bold" style={{ color: t.heading }}>My Card</h4>
          <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: t.accentSoft }}>
            <Share2 className="h-4 w-4" style={{ color: t.accent }} />
          </div>
        </div>

        <div className="mt-3 rounded-2xl overflow-hidden relative h-36 flex items-end">
          <img src={PHOTOS.sam} alt="Jordan Avery" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent 60%)" }} />
          <div className="relative p-3 text-white">
            <p className="text-base font-bold leading-tight">Jordan Avery</p>
            <p className="text-[10px] opacity-90">Managing Director · Northwind Studio</p>
          </div>
        </div>

        <div className="mt-3 space-y-2">
          {rows.map((r, i) => {
            const Icon = r.icon;
            return (
              <div key={i} className="flex items-center gap-2.5">
                <Icon className="h-3.5 w-3.5" style={{ color: t.accent }} />
                <span className="text-[11px]" style={{ color: t.heading }}>{r.v}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex justify-center">
          <div className="h-20 w-20 rounded-lg bg-white p-1.5 border border-slate-200">
            <QrArt size={72} fg="#0f172a" />
          </div>
        </div>
      </div>
      <Tabs t={t} active="Home" />
    </div>
  );
}

export function SyncScreen({ theme: t = THEMES.sepia }) {
  return (
    <div style={{ background: t.page }} className="h-full flex flex-col">
      <div style={{ background: t.bar }} className="pb-3 shrink-0">
        <StatusBar t={t} />
        <TopBar t={t} back />
      </div>
      <div className="px-4 pt-4 flex-1 overflow-hidden space-y-3">
        <div className="rounded-2xl p-3" style={{ background: t.card }}>
          <div className="flex items-start gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
              <Apple className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-[12px] font-bold" style={{ color: t.heading }}>Apple Contacts</p>
              <p className="text-[9.5px] leading-snug" style={{ color: t.sub }}>
                Bring changes in from your phone, and send yours out — you review anything that clashes.
              </p>
            </div>
          </div>
          <div className="mt-2.5 rounded-full py-2 flex items-center justify-center gap-1.5" style={{ background: t.accent }}>
            <RefreshCw className="h-3.5 w-3.5 text-white" />
            <span className="text-[11px] font-bold text-white">Sync my phone</span>
          </div>
          <div className="mt-2 rounded-full py-2 flex items-center justify-center gap-1.5 border" style={{ borderColor: t.accent }}>
            <ArrowUp className="h-3.5 w-3.5" style={{ color: t.accent }} />
            <span className="text-[10px] font-semibold" style={{ color: t.accent }}>Send my updates (200+)</span>
          </div>
          <p className="mt-2 text-[8.5px]" style={{ color: t.sub }}>Last synced 2d ago · nothing changes without your OK</p>
        </div>

        <div className="rounded-2xl p-3" style={{ background: t.card }}>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <p className="text-[12px] font-bold" style={{ color: t.heading }}>App sync · up to date</p>
          </div>
          <p className="text-[9px] mt-0.5" style={{ color: t.sub }}>Your data across every device · 2s ago</p>
        </div>

        <div className="rounded-2xl p-3" style={{ background: t.accent }}>
          <p className="text-[12px] font-bold text-white">What's new in peopleiknow</p>
          {["Ask peopleiknow — your natural-language assistant", "Review & edit auto name cleanups", "Add contacts straight to groups"].map((x) => (
            <div key={x} className="mt-1.5 flex items-start gap-1.5">
              <Check className="h-3 w-3 text-white mt-0.5 shrink-0" />
              <span className="text-[9.5px] text-white/95 leading-snug">{x}</span>
            </div>
          ))}
        </div>
      </div>
      <Tabs t={t} active="Settings" />
    </div>
  );
}

export function IntrosScreen({ theme: t = THEMES.indigo }) {
  const tabs = [
    { icon: User, label: "Basic" },
    { icon: Briefcase, label: "Work" },
    { icon: Heart, label: "Personal" },
  ];
  return (
    <div style={{ background: t.page }} className="h-full flex flex-col">
      <div style={{ background: t.bar }} className="pb-3 shrink-0">
        <StatusBar t={t} />
        <TopBar t={t} back />
      </div>
      <div className="px-4 pt-3 flex-1 overflow-hidden">
        <p className="text-[10px] leading-snug" style={{ color: t.sub }}>
          Scan my QR to connect — pick which card version they see.
        </p>
        <div className="mt-2.5 flex gap-1.5">
          {tabs.map((tb) => {
            const Icon = tb.icon;
            const on = tb.label === "Work";
            return (
              <div key={tb.label} className="flex-1 flex items-center justify-center gap-1 rounded-full py-1.5"
                style={{ background: on ? t.accent : t.accentSoft }}>
                <Icon className="h-3 w-3" style={{ color: on ? "#fff" : t.heading }} />
                <span className="text-[9.5px] font-semibold" style={{ color: on ? "#fff" : t.heading }}>{tb.label}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-3 rounded-2xl p-4 flex flex-col items-center" style={{ background: t.card }}>
          <div className="h-24 w-24 rounded-lg bg-white p-1.5">
            <QrArt size={88} fg="#1e1b4b" />
          </div>
          <div className="mt-3 rounded-full px-5 py-2 flex items-center gap-1.5" style={{ background: t.accent }}>
            <Share2 className="h-3.5 w-3.5 text-white" />
            <span className="text-[11px] font-bold text-white">Intro Link</span>
          </div>
        </div>

        <div className="mt-3 rounded-2xl p-3 flex items-center justify-between" style={{ background: t.card }}>
          <div>
            <p className="text-[11px] font-bold" style={{ color: t.heading }}>Ask for their info</p>
            <p className="text-[8.5px]" style={{ color: t.sub }}>Invite people to send their details back.</p>
          </div>
          <div className="h-5 w-9 rounded-full flex items-center px-0.5" style={{ background: t.accent, justifyContent: "flex-end" }}>
            <span className="h-4 w-4 rounded-full bg-white" />
          </div>
        </div>

        <div className="mt-2.5 rounded-2xl p-3 flex items-center gap-2.5" style={{ background: t.card }}>
          <Users className="h-4 w-4 shrink-0" style={{ color: t.accent }} />
          <div className="flex-1">
            <p className="text-[11px] font-bold" style={{ color: t.heading }}>Introduce two people</p>
            <p className="text-[8.5px]" style={{ color: t.sub }}>Send two contacts each other's card.</p>
          </div>
          <ChevronRight className="h-3.5 w-3.5" style={{ color: t.sub }} />
        </div>
      </div>
      <Tabs t={t} active="Intros" />
    </div>
  );
}
